// The timesheet engine (technical design §6.5). A tech's day is a list of transitions (facts: start
// day, dispatch, arrive, pause, non-job time, end day, home geofence, midnight). Time entries are
// derived from them by one walk in time order, so a transition that arrives late from an offline
// device is simply inserted where it happened and the day is derived again; nothing is guessed.
// Pure: the server and the device run the same code, so the device predicts the server.

import { addDays, instantAt } from '@flowology/kernel';

export type TimeEntryType = 'drive' | 'work' | 'non_job';

export type TransitionKind =
  | 'start_day'
  | 'dispatch'
  | 'undispatch'
  | 'arrive'
  | 'undo_arrive'
  | 'pause'
  | 'resume'
  | 'non_job_start'
  | 'non_job_end'
  | 'end_day'
  | 'home_geofence'
  | 'midnight'
  /** The visit went to someone else while this tech's arrival was on its way (offline): work ends. */
  | 'reassigned'
  /** The office ends the part left open (0200, rules §9.1): no flag, and the day goes on. */
  | 'office_end'
  /** The office's part inside another ends, and the part it interrupted goes on (lunch inside a job). */
  | 'office_resume';

export interface TimeTransition {
  id: string; // also the id of the entry it opens
  kind: TransitionKind;
  at: string; // instant
  seq: number; // tie-break for equal instants: arrival order
  appointmentId: string | null;
  jobId: string | null;
  /** dispatch: after-hours call-out (opens a drive even off duty). */
  afterHours?: boolean;
  /** pause: the last after-hours job of the night, so a paid drive home starts. */
  returnHome?: boolean;
  /** arrive: manual tap or geofence. */
  trigger?: 'manual' | 'geofence';
  /** non_job_start */
  nonJobCode?: string | null;
  paid?: boolean;
  /** Added by the office (time.editDay, 0200): what it closes isn't flagged; the office looked. */
  office?: boolean;
  /** A moved tap (0200): this row gives the time of the tap `replaces`, which keeps its id and kind. */
  replaces?: string | null;
}

export interface DerivedEntry {
  id: string;
  type: TimeEntryType;
  startedAt: string;
  endedAt: string | null;
  appointmentId: string | null;
  jobId: string | null;
  nonJobCode: string | null;
  paid: boolean;
  returnHome: boolean;
  startTrigger: string;
  endTrigger: string | null;
  /** The tap that ended it; null when the day's midnight closed it with no tap. */
  endedBy: string | null;
  flagged: boolean;
  flagReason: string | null;
}

/** A flagged time entry's reason in words, for payroll and review (one wording, the server's). */
export const TIME_FLAG_WORDS: Record<string, string> = {
  closed_by_dispatch: 'work time was still running when the next visit was dispatched',
  open_at_end_of_day: 'work time was still running when the day was ended',
  arrive_undone: 'an arrival was undone',
  reassigned_while_working: 'work time was still running when the visit was given to someone else',
  closed_by_non_job: 'work time was ended by a lunch, shop or supply-house tap',
  closed_by_arrival_elsewhere: 'work time was still running when the next visit began',
  auto_end_of_day: 'time was still running at midnight and was closed there',
  // DEC-013: the whole shift counts on the day it started (ESA s.1(1) "day" (b))
  past_midnight: 'time ran past midnight: it all counts on the day it started',
};

/** The words for a flag reason; an unknown code as words, never the bare code. */
export const timeFlagWords = (code: string): string => TIME_FLAG_WORDS[code] ?? code.replace(/_/g, ' ');

export interface DerivedDay {
  entries: DerivedEntry[];
  startedAt: string | null;
  endedAt: string | null;
  endTrigger: string | null;
  /** Transitions that changed nothing, with why (shown to the office, never silently dropped). */
  ignored: { id: string; reason: string }[];
}

export const byTime = (a: TimeTransition, b: TimeTransition) =>
  a.at < b.at ? -1 : a.at > b.at ? 1 : a.seq - b.seq;

/**
 * The taps as they now stand (0200): a moved tap takes the time of its latest move and keeps its own
 * id, kind and place in arrival order, so the part it opens keeps its id; the move rows themselves go.
 */
export function effectiveTransitions(transitions: readonly TimeTransition[]): TimeTransition[] {
  const moved = new Map<string, TimeTransition>();
  for (const t of [...transitions].sort((a, b) => a.seq - b.seq)) if (t.replaces) moved.set(t.replaces, t);
  return transitions
    .filter((t) => !t.replaces)
    .map((t) => (moved.has(t.id) ? { ...t, at: moved.get(t.id)!.at } : t))
    .sort(byTime);
}

/**
 * A part still open at the day's midnight that the next day's first tap ends (an End day at 00:30):
 * the minutes after midnight are the next day's, as a part of their own from its midnight, so none are
 * lost. Which day a shift past midnight counts on is DEC-013 (Amir's call): see pastMidnightRule.
 */
export interface CarryIn {
  /** Its own id, made from the part it goes on from (carryId). */
  id: string;
  /** The next day's midnight. */
  at: string;
  type: TimeEntryType;
  appointmentId: string | null;
  jobId: string | null;
  nonJobCode: string | null;
  paid: boolean;
  returnHome: boolean;
  /** The tap that ends it is the office's (a time typed past midnight, asked about first). */
  endedByOffice: boolean;
}

export interface DeriveOptions {
  /** The part that went on past the previous day's midnight (carryInto). */
  carryIn?: CarryIn | null;
  /** This day's open part goes on into the next day: it is split at midnight, with this flag or none. */
  carriesOver?: { flag: string | null } | null;
}

/**
 * DEC-013 (Amir, 26 Oct): where a shift past midnight counts. 'day_it_started': the whole shift is
 * the day it started, for its daily overtime and on the Timesheet, as BC's Employment Standards Act
 * reads a day for a shift that continues over midnight (s.1(1) "day" (b): the 24 hours from the
 * start of the shift). 'split_at_midnight' (the rule before) split it there, each side on its own day.
 */
export type PastMidnightRule = 'split_at_midnight' | 'day_it_started';
export function pastMidnightRule(): PastMidnightRule {
  return 'day_it_started';
}

/** Taps that end the open part without starting another: the ones that can end a part from the day before. */
const ENDS_ONLY: ReadonlySet<TransitionKind> = new Set([
  'end_day',
  'pause',
  'non_job_end',
  'home_geofence',
  'reassigned',
  'undispatch',
  'office_end',
]);

/** A carried part's id: the part it goes on from, with UUID version 8 (ids are v7, so it can't collide). */
export const carryId = (id: string) => `${id.slice(0, 14)}8${id.slice(15)}`;

/**
 * The part the previous day left open at its midnight, when this day's first tap ends it (and only
 * then: a day that starts with Start day, or with a new visit, leaves the old part closed at midnight
 * and flagged as before). `dayStart` is this day's midnight.
 */
export function carryInto(
  prev: readonly TimeTransition[],
  cur: readonly TimeTransition[],
  dayStart: string,
): CarryIn | null {
  const open = openEntry(deriveDay(prev.filter((t) => t.kind !== 'midnight')));
  if (!open) return null;
  const first = effectiveTransitions(cur)[0];
  if (!first || first.at < dayStart || !endsOpenPart(open, first)) return null;
  return {
    id: carryId(open.id),
    at: dayStart,
    type: open.type,
    appointmentId: open.appointmentId,
    jobId: open.jobId,
    nonJobCode: open.nonJobCode,
    paid: open.paid,
    returnHome: open.returnHome,
    endedByOffice: !!first.office,
  };
}

/** Whether a tap ends the part left open without starting another (ENDS_ONLY, and it fits the part). */
function endsOpenPart(open: DerivedEntry, first: TimeTransition): boolean {
  if (!ENDS_ONLY.has(first.kind)) return false;
  const sameVisit = open.appointmentId === first.appointmentId;
  return first.kind === 'pause' || first.kind === 'reassigned'
    ? open.type === 'work' && sameVisit
    : first.kind === 'non_job_end'
      ? open.type === 'non_job'
      : first.kind === 'home_geofence'
        ? open.type === 'drive' && open.returnHome
        : first.kind === 'undispatch'
          ? open.type === 'drive' && sameVisit
          : true;
}

/**
 * DEC-013 'day_it_started': the taps of the next day that go on with this day's shift past its
 * midnight, so they count on this day. There are some only when the next day's first tap goes on
 * with the part left open at midnight: it ends it (carryInto's rule), or it is the arrival at the
 * visit being driven to (a late call reached after midnight). From there each tap goes on with the
 * shift while a part is still open before it, and an End day with nothing open ends it. A next day
 * that starts afresh (Start day, another visit) leaves this one closed at its midnight, flagged, as
 * before. `nextStart` is the next day's midnight. The next day's own midnight close never goes back.
 */
export function carriedBack(
  cur: readonly TimeTransition[],
  next: readonly TimeTransition[],
  nextStart: string,
): TimeTransition[] {
  const own = effectiveTransitions(cur).filter((t) => t.kind !== 'midnight');
  let state = deriveDay(own);
  const open = openEntry(state);
  if (!open) return [];
  const taps = effectiveTransitions(next).filter((t) => t.at >= nextStart);
  const first = taps[0];
  const arrivesThere =
    first?.kind === 'arrive' && open.type === 'drive' && open.appointmentId === first.appointmentId;
  if (!first || !(endsOpenPart(open, first) || arrivesThere)) return [];
  const out: TimeTransition[] = [];
  for (const t of taps) {
    if (t.kind === 'midnight') break;
    const onDuty = state.startedAt !== null && state.endedAt === null;
    if (!openEntry(state) && !(onDuty && t.kind === 'end_day')) break;
    out.push(t);
    state = deriveDay([...own, ...out]);
  }
  return out;
}

/**
 * One day derived as the server keeps it (and the device predicts it), from its taps and the days
 * either side of it, whose taps decide where a shift past midnight counts (DEC-013, pastMidnightRule).
 * Closed at its midnight once it's over: with 'day_it_started', a shift that goes on past midnight
 * instead takes the next day's taps that go on with it (carriedBack), and the next day is derived
 * without them.
 */
export function deriveStoredDay(
  date: string,
  tz: string,
  today: string,
  days: { prev: readonly TimeTransition[]; cur: readonly TimeTransition[]; next: readonly TimeTransition[] },
): DerivedDay {
  const dayStart = instantAt(date, '00:00', tz);
  const nextStart = instantAt(addDays(date, 1), '00:00', tz);
  const endOfDay = date < today ? nextStart : undefined;
  if (pastMidnightRule() === 'split_at_midnight') {
    const carryIn = carryInto(days.prev, days.cur, dayStart);
    const carryOut = carryInto(days.cur, days.next, nextStart);
    return deriveDay(days.cur, endOfDay, {
      carryIn,
      carriesOver: carryOut ? { flag: splitFlag(days.cur, carryOut) } : null,
    });
  }
  // the day before's shift took this day's first taps: they're its, not this day's
  const taken = new Set(carriedBack(days.prev, days.cur, dayStart).map((t) => t.id));
  const cur = effectiveTransitions(days.cur).filter((t) => !taken.has(t.id));
  const later = carriedBack(cur, days.next, nextStart);
  if (!later.length) return deriveDay(cur, endOfDay);
  // Past midnight: the shift is this day's to its end. Still open after the next day's taps, it
  // closes at the next day's midnight once that day is over (a missed clock-out, flagged).
  const nextDate = addDays(date, 1);
  const day = deriveDay(
    [...cur.filter((t) => t.kind !== 'midnight'), ...later],
    nextDate < today ? instantAt(addDays(date, 2), '00:00', tz) : undefined,
  );
  // the bookkeeper checks a shift past midnight on a normal day, as the quick edit asks before such an
  // end: not on a late call (an after-hours call-out or its drive home), nor an end the office typed
  const crossed = day.entries.find(
    (e) => e.startedAt < nextStart && e.endedAt !== null && e.endedAt > nextStart,
  );
  const lateCall = [...cur, ...later].some((t) => t.afterHours || t.returnHome);
  const typed = later.some((t) => t.office);
  if (crossed && !crossed.flagged && !lateCall && !typed) {
    crossed.flagged = true;
    crossed.flagReason = 'past_midnight';
  }
  return day;
}

/**
 * The flag on the side before midnight when a part is split there: none on a late call (an
 * after-hours call-out, or its paid drive home) or when the office typed the end itself; otherwise
 * the bookkeeper checks it, as the quick edit asks before saving such an end.
 */
export function splitFlag(prev: readonly TimeTransition[], carry: CarryIn): string | null {
  if (carry.endedByOffice || prev.some((t) => t.afterHours || t.returnHome)) return null;
  return 'past_midnight';
}

/**
 * Derives one tech-day. `endOfDay` (the next local midnight) is given only for days that are over:
 * anything still open then is closed at midnight and flagged (the midnight auto end-of-day).
 */
export function deriveDay(
  transitions: readonly TimeTransition[],
  endOfDay?: string,
  opts: DeriveOptions = {},
): DerivedDay {
  const entries: DerivedEntry[] = [];
  const ignored: DerivedDay['ignored'] = [];
  // Held in an object so the helpers below and the loop see the same, un-narrowed value.
  const st: { cur: DerivedEntry | null } = { cur: null };
  let startedAt: string | null = null;
  let endedAt: string | null = null;
  let endTrigger: string | null = null;
  const onDuty = () => startedAt !== null && endedAt === null;

  // The part an office part was put inside, to go on once it ends (office_resume).
  let interrupted: DerivedEntry | null = null;
  const close = (at: string, trigger: string, flag?: string, endedBy: string | null = null) => {
    if (!st.cur) return;
    st.cur.endedAt = at < st.cur.startedAt ? st.cur.startedAt : at;
    st.cur.endTrigger = trigger;
    st.cur.endedBy = endedBy;
    if (flag) {
      st.cur.flagged = true;
      st.cur.flagReason = flag;
    }
    st.cur = null;
  };
  const open = (tr: TimeTransition, type: TimeEntryType, extra: Partial<DerivedEntry> = {}) => {
    st.cur = {
      id: tr.id,
      type,
      startedAt: tr.at,
      endedAt: null,
      appointmentId: tr.appointmentId,
      jobId: tr.jobId,
      nonJobCode: null,
      paid: true,
      returnHome: false,
      startTrigger: tr.kind === 'arrive' && tr.trigger === 'geofence' ? 'geofence' : tr.kind,
      endTrigger: null,
      endedBy: null,
      flagged: false,
      flagReason: null,
      ...extra,
    };
    entries.push(st.cur);
  };
  const startDay = (at: string) => {
    if (startedAt === null) startedAt = at;
  };
  const isCur = (type: TimeEntryType, appointmentId: string | null) =>
    st.cur !== null && st.cur.type === type && st.cur.appointmentId === appointmentId;

  if (opts.carryIn) {
    const c = opts.carryIn;
    startedAt = c.at;
    st.cur = {
      id: c.id,
      type: c.type,
      startedAt: c.at,
      endedAt: null,
      appointmentId: c.appointmentId,
      jobId: c.jobId,
      nonJobCode: c.nonJobCode,
      paid: c.paid,
      returnHome: c.returnHome,
      startTrigger: 'midnight',
      endTrigger: null,
      endedBy: null,
      flagged: false,
      flagReason: null,
    };
    entries.push(st.cur);
  }
  // What the midnight close flags: a split when the part goes on into the next day.
  const midnightFlag = opts.carriesOver ? (opts.carriesOver.flag ?? undefined) : 'auto_end_of_day';

  for (const tr of effectiveTransitions(transitions)) {
    // The office's taps flag nothing they close: the office looked (rules §9.1).
    const flag = (f: string | undefined) => (tr.office ? undefined : f);
    const end = (trigger: string, f?: string) => close(tr.at, trigger, flag(f), tr.id);
    switch (tr.kind) {
      case 'start_day':
        if (onDuty()) {
          ignored.push({ id: tr.id, reason: 'day_already_started' });
          break;
        }
        if (endedAt !== null) {
          endedAt = null; // started again after ending (forgot something): the day goes on
          endTrigger = null;
        }
        startDay(tr.at);
        if (tr.appointmentId && !st.cur) open(tr, 'drive');
        break;
      case 'dispatch':
        if (isCur('drive', tr.appointmentId)) {
          ignored.push({ id: tr.id, reason: 'already_driving_there' });
          break;
        }
        if (!onDuty() && !tr.afterHours) {
          ignored.push({ id: tr.id, reason: 'off_duty' });
          break;
        }
        end('dispatch', st.cur?.type === 'work' ? 'closed_by_dispatch' : undefined);
        open(tr, 'drive');
        break;
      case 'undispatch':
        if (isCur('drive', tr.appointmentId)) end('undispatch');
        else ignored.push({ id: tr.id, reason: 'not_driving_there' });
        break;
      case 'arrive':
      case 'resume':
        if (isCur('work', tr.appointmentId)) {
          ignored.push({ id: tr.id, reason: 'already_working_there' });
          break;
        }
        startDay(tr.at);
        end(tr.kind, st.cur?.type === 'work' ? 'closed_by_arrival_elsewhere' : undefined);
        open(tr, 'work');
        break;
      case 'undo_arrive':
        if (st.cur !== null && isCur('work', tr.appointmentId)) {
          const c: DerivedEntry = st.cur;
          c.type = 'drive';
          c.flagged = true;
          c.flagReason = 'arrive_undone';
        } else ignored.push({ id: tr.id, reason: 'not_working_there' });
        break;
      case 'pause':
        if (!isCur('work', tr.appointmentId)) {
          ignored.push({ id: tr.id, reason: 'not_working_there' });
          break;
        }
        end('pause');
        if (tr.returnHome) open(tr, 'drive', { returnHome: true });
        break;
      case 'non_job_start':
        startDay(tr.at);
        interrupted = tr.office && st.cur ? { ...st.cur } : null;
        end('non_job_start', st.cur?.type === 'work' ? 'closed_by_non_job' : undefined);
        open(tr, 'non_job', {
          appointmentId: null,
          jobId: null,
          nonJobCode: tr.nonJobCode ?? null,
          paid: tr.paid ?? true,
        });
        break;
      case 'non_job_end':
        if (st.cur?.type === 'non_job') end('non_job_end');
        else ignored.push({ id: tr.id, reason: 'no_non_job_time' });
        break;
      case 'end_day':
        end('end_day', st.cur?.type === 'work' ? 'open_at_end_of_day' : undefined);
        startDay(tr.at);
        endedAt = tr.at;
        endTrigger = 'end_day';
        break;
      case 'home_geofence':
        if (st.cur?.type === 'drive' && st.cur.returnHome) end('home_geofence');
        else ignored.push({ id: tr.id, reason: 'not_driving_home' });
        break;
      case 'reassigned':
        if (isCur('work', tr.appointmentId)) end('reassigned', 'reassigned_while_working');
        else ignored.push({ id: tr.id, reason: 'not_working_there' });
        break;
      case 'midnight':
        if (st.cur) end('midnight', midnightFlag);
        if (onDuty()) {
          endedAt = tr.at;
          endTrigger = 'midnight';
        }
        break;
      case 'office_end':
        if (st.cur) end('office_end');
        else ignored.push({ id: tr.id, reason: 'nothing_open' });
        break;
      case 'office_resume': {
        const was: DerivedEntry | null = interrupted;
        if (st.cur?.type !== 'non_job' || !was) {
          ignored.push({ id: tr.id, reason: 'nothing_to_resume' });
          break;
        }
        end('office_resume');
        open(tr, was.type, {
          appointmentId: was.appointmentId,
          jobId: was.jobId,
          nonJobCode: was.nonJobCode,
          paid: was.paid,
          returnHome: was.returnHome,
        });
        interrupted = null;
        break;
      }
    }
  }
  if (endOfDay && (st.cur || onDuty())) {
    close(endOfDay, 'midnight', midnightFlag);
    if (onDuty()) {
      endedAt = endOfDay;
      endTrigger = 'midnight';
    }
  }
  return { entries, startedAt, endedAt, endTrigger, ignored };
}

/** The entry open at the end of the derived day, if any. */
export function openEntry(day: DerivedDay): DerivedEntry | null {
  const last = day.entries.at(-1);
  return last && last.endedAt === null ? last : null;
}

/** Totals in milliseconds by kind, up to `now` for an open entry. */
export function dayTotals(
  day: DerivedDay,
  now: string,
): { drive: number; work: number; nonJobPaid: number; unpaid: number } {
  const out = { drive: 0, work: 0, nonJobPaid: 0, unpaid: 0 };
  for (const e of day.entries) {
    const ms = Date.parse(e.endedAt ?? now) - Date.parse(e.startedAt);
    if (ms <= 0) continue;
    if (!e.paid) out.unpaid += ms;
    else if (e.type === 'non_job') out.nonJobPaid += ms;
    else out[e.type] += ms;
  }
  return out;
}
